import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import type { PropertiesListResponse } from '@lasoxp/contracts';
import type { Property } from '../types';
import { devError } from '../utils/dev-log';

/**
 * Loads properties via the backend API (`GET /v1/admin/properties`).
 *
 * The backend resolves scope from the caller's memberships — a suspended user
 * with a stale Firebase token will be rejected by auth-v2 middleware before
 * reaching the properties query.
 */
export function usePropertyLoader() {
  const { adminUser } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);

  const loadProperties = useCallback(async () => {
    if (!adminUser) {
      setProperties([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const resp = await apiService.get<PropertiesListResponse & { truncated?: boolean }>(
        `${API_CONFIG.endpoints.properties.list}?limit=200`,
      );
      setProperties((resp.properties ?? []) as Property[]);
      setTruncated(!!resp.truncated);
    } catch (error) {
      devError('[usePropertyLoader] Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  }, [adminUser]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  return { properties, loading, truncated, reload: loadProperties };
}
